import React, { useState, useEffect } from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import styled, { useTheme } from 'styled-components';

import { Country } from '@common/types/data/country';

import AppState from '@webapp/store/state';
import { ProductSample } from '@webapp/store/merch/state';
import { ValidateMerchProductSample } from '@webapp/store/merch/action-creators';

import CountriesCntrl from '@webapp/controllers/form/email-or-phone/countries-merch';

import Box from '@components/layout/box';
import Grid from '@components/basics/grid';
import InputField from '@components/form/input';
import AddIcon from '@components/basics/icons/add';
import BoxTitleHeader from '@components/layout/box-title-header';
import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';

interface ShippingDetailsStateProps {
  productSample?: ProductSample | null | undefined;
  validateSampleOrder: Function;
  hasItemBelow?: boolean;
  userData: {
    fullName?: string;
    country?: Country;
    city?: string;
    state?: string;
    address?: string;
    address2?: string;
    postalCode?: string;
  };
}

export const DEFAULT_COUNTRY: Country = {
  id: 'us',
  name: 'United States',
  code: '1',
};

const mapStateToProps = (state: AppState) => {
  const productSample = state.merch.active?.productSample;
  return {
    productSample,
    userData: {
      fullName: state.user.profile?.fullName || '',
      country: state.user.profile?.country,
      city: state.user.profile?.address?.city || '',
      state: state.user.profile?.address?.state || '',
      address: state.user.profile?.address?.address || '',
      address2: state.user.profile?.address?.address2 || '',
      postalCode: state.user.profile?.address?.postalCode || '',
    },
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  validateSampleOrder: (sampleOrder: ProductSample) =>
    dispatch(new ValidateMerchProductSample(sampleOrder)),
});

const ShippingDetailsPage = ({
  validateSampleOrder,
  productSample,
  hasItemBelow,
  userData,
}: ShippingDetailsStateProps) => {
  const theme = useTheme();

  const [userName, setUserName] = useState(userData.fullName);
  const [shippingAddress, setAddress] = useState(userData.address);
  const [shippingAddress2, setAddress2] = useState(userData.address2);
  const [shippingCity, setCity] = useState(userData.city);
  const [shippingPostCode, setPostCode] = useState(userData.postalCode);
  const [hasAddress2, setHasAddress2] = useState<boolean>(false);
  const [shippingCountry, setCountry] = useState<Country>(
    userData.country || DEFAULT_COUNTRY
  );
  const [shippingRegion, setRegion] = useState(userData.state);

  useEffect(() => {
    validateSampleOrder({
      ...productSample,
      shippingDetails: {
        ...productSample?.shippingDetails,
        country: shippingCountry.id.toUpperCase(),
        name: userName,
        address: shippingAddress,
        city: shippingCity,
        postCode: shippingPostCode,
        region: shippingRegion,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateReceiveSampleOrder = (
    key: 'address' | 'address2' | 'city' | 'name' | 'postCode' | 'region',
    value: string
  ) => {
    if (key === 'name') {
      setUserName(value);
    }
    if (key === 'address') {
      setAddress(value);
    }
    if (key === 'address2') {
      setAddress2(value);
    }
    if (key === 'city') {
      setCity(value);
    }
    if (key === 'postCode') {
      setPostCode(value);
    }
    if (key === 'region') {
      setRegion(value);
    }
    validateSampleOrder({
      ...productSample,
      shippingDetails: { ...productSample?.shippingDetails, [key]: value },
    });
  };

  const handleCountryChange = (key: string, country: Country) => {
    setCountry(country);
    validateSampleOrder({
      ...productSample,
      shippingDetails: {
        ...productSample?.shippingDetails,
        [key]: country.id.toUpperCase(),
      },
    });
  };

  return (
    <Box noBorderTop noBorderBottom={hasItemBelow}>
      <BoxTitleHeader
        className="mb-20"
        title={<Translate id="MERCH.PRODUCT_SAMPLE.SHIPPING_DETAILS.TITLE" />}
        text={<Translate id="MERCH.PRODUCT_SAMPLE.SHIPPING_DETAILS.TEXT" />}
      />

      <Grid gap={5}>
        <InputField
          label={<Translate id="MERCH.PRODUCT_SAMPLE.INPUTS.FULLNAME" />}
          value={userName}
          error={''}
          onChange={(val) => updateReceiveSampleOrder('name', val)}
        />

        <Grid gap={5}>
          <InputWrapper justifyContent="space-between" alignItems="center">
            <CountriesCntrl
              className="input"
              type="telephone"
              country={shippingCountry}
              onCountryChange={(value) => handleCountryChange('country', value)}
            />

            <InputField
              className="input"
              label={<Translate id="MERCH.PRODUCT_SAMPLE.INPUTS.STATE" />}
              value={shippingRegion}
              onChange={(val) => updateReceiveSampleOrder('region', val)}
            />
          </InputWrapper>

          <InputWrapper justifyContent="space-between" alignItems="center">
            <InputField
              className="input"
              label={<Translate id="MERCH.PRODUCT_SAMPLE.INPUTS.CITY" />}
              value={shippingCity}
              onChange={(val) => updateReceiveSampleOrder('city', val)}
            />
            <InputField
              className="input"
              label={<Translate id="MERCH.PRODUCT_SAMPLE.INPUTS.CODE" />}
              value={shippingPostCode}
              onChange={(val) => updateReceiveSampleOrder('postCode', val)}
            />
          </InputWrapper>

          <InputField
            className="input"
            value={shippingAddress}
            label={<Translate id="MERCH.PRODUCT_SAMPLE.INPUTS.ADDRESS.1" />}
            onChange={(val) => updateReceiveSampleOrder('address', val)}
          />
          {!hasAddress2 ? (
            <AddAddress
              justifyContent="flex-start"
              alignItems="center"
              gap={10}
              onClick={() => setHasAddress2(true)}
            >
              <AddIcon size={16} circleColor={theme.colors.black} />
              <Typography weight="700">
                <Translate id="MERCH.PRODUCT_SAMPLE.INPUTS.ADDRESS.NEW" />
              </Typography>
            </AddAddress>
          ) : (
            <InputField
              className="input"
              hasClose
              onClose={() => setHasAddress2(false)}
              label={<Translate id="MERCH.PRODUCT_SAMPLE.INPUTS.ADDRESS.2" />}
              value={shippingAddress2}
              onChange={(val) => updateReceiveSampleOrder('address2', val)}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ShippingDetailsPage);

const InputWrapper = styled(Flex)`
  gap: 5px;

  .input {
    flex-grow: 1;
    flex-basis: 50%;
  }
`;

const AddAddress = styled(Flex)`
  margin-top: 20px;
  cursor: pointer;
  opacity: 1;
  transition: opacity
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    opacity: 0.5;
  }
`;
